import React, { useEffect, useState } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import InvoiceListLayer from "../components/InvoiceListLayer";
import DashboardFilter from "../components/DashboardFilter";
import { API_URL } from "..";
import { Spinner } from "react-bootstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

const DEFAULT_PAGINATION = {
  page: 1,
  limit: 25,
  total: 0,
  totalPages: 1,
};

const InvoiceListStatusPage = () => {
  const { status } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const selectedStatus = status || "all";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);

  const [dateFilter, setDateFilter] = useState(
    searchParams.get("filter") || "all"
  );

  const [customRange, setCustomRange] = useState({
    startDate: searchParams.get("startDate") || "",
    endDate: searchParams.get("endDate") || "",
  });

  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");

  const getStatusTitle = () => {
    switch (selectedStatus) {
      case "all":
        return "All Invoices";
      case "paid":
        return "Paid Invoices";
      case "overdue":
        return "Overdue Invoices";
      case "unpaid":
        return "Unpaid Invoices";
      default:
        return "Invoices";
    }
  };

  const updateUrlParams = ({
    filter = dateFilter,
    range = customRange,
    page = pagination.page,
    limit = pagination.limit,
    search = searchTerm,
  }) => {
    const params = new URLSearchParams();

    params.set("filter", filter);
    params.set("page", String(page));
    params.set("limit", String(limit));

    if (search) {
      params.set("search", search);
    }

    if (filter === "custom") {
      if (range.startDate) {
        params.set("startDate", range.startDate);
      }

      if (range.endDate) {
        params.set("endDate", range.endDate);
      }
    }

    navigate(`/invoices/${selectedStatus}?${params.toString()}`, {
      replace: false,
    });
  };

  const fetchInvoices = async ({
    invoiceStatus = selectedStatus,
    page = 1,
    limit = 25,
    filter = "all",
    range = { startDate: "", endDate: "" },
    search = "",
  }) => {
    setLoading(true);

    try {
      const user = JSON.parse(localStorage.getItem("user"));

      if (!user) {
        window.location.href = "/sign-in";
        return;
      }

      const params = new URLSearchParams();

      params.set("invoiceStatus", invoiceStatus);
      params.set("page", String(page));
      params.set("limit", String(limit));
      params.set("filter", filter);

      if (search) {
        params.set("search", search);
      }

      if (filter === "custom" && range.startDate && range.endDate) {
        params.set("startDate", range.startDate);
        params.set("endDate", range.endDate);
      }

      const response = await fetch(
        `${API_URL}/api/invoice/get/invoice?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Accept: "*/*",
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();

      if (response.status === 200) {
        setData(result.invoices || []);
        setPagination(result.pagination || DEFAULT_PAGINATION);
      } else {
        setData([]);
        setPagination(DEFAULT_PAGINATION);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      setData([]);
      setPagination(DEFAULT_PAGINATION);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const filterFromUrl = searchParams.get("filter") || "all";
    const startDateFromUrl = searchParams.get("startDate") || "";
    const endDateFromUrl = searchParams.get("endDate") || "";
    const pageFromUrl = Number(searchParams.get("page")) || 1;
    const limitFromUrl = Number(searchParams.get("limit")) || 25;
    const searchFromUrl = searchParams.get("search") || "";

    const rangeFromUrl = {
      startDate: startDateFromUrl,
      endDate: endDateFromUrl,
    };

    setDateFilter(filterFromUrl);
    setCustomRange(rangeFromUrl);
    setSearchTerm(searchFromUrl);

    fetchInvoices({
      invoiceStatus: selectedStatus,
      page: pageFromUrl,
      limit: limitFromUrl,
      filter: filterFromUrl,
      range: rangeFromUrl,
      search: searchFromUrl,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus, searchParams.toString()]);

  const handleFilterChange = (newFilter) => {
    setDateFilter(newFilter);

    if (newFilter !== "custom") {
      updateUrlParams({
        filter: newFilter,
        range: {
          startDate: "",
          endDate: "",
        },
        page: 1,
        limit: pagination.limit,
        search: searchTerm,
      });
    }
  };

  const handleCustomApply = () => {
    if (!customRange.startDate || !customRange.endDate) return;

    updateUrlParams({
      filter: "custom",
      range: customRange,
      page: 1,
      limit: pagination.limit,
      search: searchTerm,
    });
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleSearchSubmit = () => {
    updateUrlParams({
      filter: dateFilter,
      range: customRange,
      page: 1,
      limit: pagination.limit,
      search: searchTerm,
    });
  };

  const handlePageChange = (newPage) => {
    updateUrlParams({
      filter: dateFilter,
      range: customRange,
      page: newPage,
      limit: pagination.limit,
      search: searchTerm,
    });
  };

  const handleLimitChange = (newLimit) => {
    updateUrlParams({
      filter: dateFilter,
      range: customRange,
      page: 1,
      limit: newLimit,
      search: searchTerm,
    });
  };

  const refreshCurrentPage = () => {
    fetchInvoices({
      invoiceStatus: selectedStatus,
      page: pagination.page,
      limit: pagination.limit,
      filter: dateFilter,
      range: customRange,
      search: searchTerm,
    });
  };

  return (
    <>
      <MasterLayout>
        <Breadcrumb title={getStatusTitle()} />

        <div className="mt-4 mb-20">
          <div className="card p-3 border radius-8">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div>
                <h6 className="mb-1">{getStatusTitle()}</h6>

                <p className="mb-0 text-secondary-light text-sm">
                  Current filter:{" "}
                  <span className="fw-semibold text-primary">
                    {dateFilter === "custom"
                      ? `${customRange.startDate || "-"} to ${
                          customRange.endDate || "-"
                        }`
                      : dateFilter}
                  </span>
                </p>
              </div>

              <div className="text-end">
                <span className="badge bg-primary">
                  {pagination.total || 0} Invoices
                </span>
              </div>
            </div>

            <div className="mt-3">
              <DashboardFilter
                filter={dateFilter}
                onFilterChange={handleFilterChange}
                customRange={customRange}
                setCustomRange={setCustomRange}
                onCustomApply={handleCustomApply}
                loading={loading}
              />
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-center mt-4">
            <Spinner />
          </div>
        )}

        {!loading && data.length === 0 && (
          <p className="text-center mt-4">No Invoices Found....</p>
        )}

        {!loading && data.length > 0 && (
          <InvoiceListLayer
            jobsData={data}
            pagination={pagination}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            loading={loading}
            searchTerm={searchTerm}
            onSearchTermChange={handleSearchChange}
            onSearchSubmit={handleSearchSubmit}
            onRefresh={refreshCurrentPage}
          />
        )}
      </MasterLayout>
    </>
  );
};

export default InvoiceListStatusPage;